<script setup lang="ts">
import debounce from 'lodash/debounce'

interface Props {
  search?: boolean
}

interface FilterParams {
  type: string
  path: string
  search?: boolean
}

const { search } = defineProps<Props>()
const categoryStore = useCategoryStore()
const { min, max, open, checked, priceMin, priceMax, properties } = storeToRefs(categoryStore)
const { query, path } = useRoute()
const store = useStore()
const { filterLoading } = storeToRefs(store)

onMounted(() => {
  categoryStore.getChecked({ query })
})

const debouncedLoad = debounce(async (params: FilterParams) => {
  try {
    filterLoading.value = true
    await categoryStore.load(params)
  } finally {
    filterLoading.value = false
  }
}, 500)

const handleFilterChange = (params: FilterParams) => {
  debouncedLoad(params)
}
</script>

<template>
  <div v-if="filterLoading" class="fixed inset-0 z-50 flex items-center justify-center bg-white/70 backdrop-blur-sm">
    <LoadingIndicator class="w-[100px] h-[100px]" />
  </div>
  <div class="relative" :class="{ 'opacity-50': filterLoading }">
    <div v-for="(filter, index) in properties" :key="filter.id">
      <div class="flex justify-between items-center border-b border-solid border-gray-400 my-[15px] pb-[10px]">
        <span class="text-18 font-medium">{{ filter.name }}</span>
        <span
          v-if="index === 0 && (min || max || checked.length !== 0)"
          class="text-12 uppercase cursor-pointer"
          @click="handleFilterChange({ type: 'remove', path, search })"
        >
          Wyczyść filtry
        </span>
      </div>
      <ul class="flex flex-col items-start">
        <li
          v-for="(data, index2) in filter.options"
          :key="data.id"
          :class="{ hidden: index2 >= 4 && !open.includes(filter.id), 'opacity-40 cursor-not-allowed': data.count === 0 }"
          class="flex flex-row-reverse items-center gap-[15px] text-14 py-[10px]"
        >
          <label :for="data.id" class="cursor-pointer select-none">
            {{ tor(data.name) }}
          </label>
          <input
            :id="data.id"
            v-model="checked"
            :value="data.id"
            type="checkbox"
            :disabled="data.count === 0"
            @change="handleFilterChange({ type: 'properties', path, search })"
          >
        </li>
      </ul>
      <div
        v-if="filter.options.length > 4 && !open.includes(filter.id)"
        class="flex items-center gap-[15px] cursor-pointer py-[10px]"
        @click="categoryStore.openFilter(filter.id)"
      >
        <i class="icon-arrow-menu text-12 rotate-90" />
        <span class="text-14">Pokaż wszystko ({{ filter.options.length - 4 }})</span>
      </div>
    </div>
    <div>
      <div class="flex justify-between items-center border-b border-solid border-gray-400 my-[15px] pb-[10px]">
        <span class="text-18 font-medium">Cena</span>
      </div>
      <ul class="flex gap-[10px]">
        <li class="flex items-center w-full border border-solid border-gray-500 rounded-[5px]">
          <input
            v-model="min"
            type="number"
            :placeholder="priceMin.toString()"
            :min="priceMin"
            :max="priceMax"
            step="50"
            class="w-full bg-transparent pl-[10px] py-[8px]"
            @change="handleFilterChange({ type: 'min-price', path, search })"
          >
          <span class="pr-[10px]">zł</span>
        </li>
        <li class="flex items-center w-full border border-solid border-gray-500 rounded-[5px]">
          <input
            v-model="max"
            type="number"
            :placeholder="priceMax.toString()"
            :min="priceMin"
            :max="priceMax"
            step="50"
            class="w-full bg-transparent pl-[10px] py-[8px]"
            @change="handleFilterChange({ type: 'max-price', path, search })"
          >
          <span class="pr-[10px]">zł</span>
        </li>
      </ul>
    </div>
  </div>
</template>
